import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useEndpoints } from "../../api/ApiContext";
import { ApiError } from "../../api/errors";
import type { CampaignDetail, UpdateBudgetResponse, UpdateScheduleResponse } from "../../api/types";
import { CommandError } from "../../components/CommandError";
import { Banner, Button, Card, Dialog, Input, Money, MoneyStat, useToast } from "../../components/ui";
import { formatLocal, formatUtc, toRfc3339Utc, viewerTimeZone } from "../../lib/datetime";
import { amountValidationMessage, compareDecimal, validateAmountInput } from "../../lib/money";
import { useIntentKey } from "../../lib/useIntentKey";
import { RESUME_BLOCKED, resumeBlockedMessage } from "./campaignCopy";
import { resolveField, TimeField, toLocalInput, type TimeChoice } from "./scheduleFields";

// The campaign's own settings on the status page (UX-DESIGN.md §2.3 step 1,
// §2.3 step 3, §2.4): name (G-14), schedule (G-15), budget (G-16), and
// Pause / Resume / End (G-15). Every write carries one idempotency key per
// intent (lib/useIntentKey), so a retry or a step-up retry replays rather
// than acting twice (G-21), and refusals are typed 422s (G-22).

function useRefreshCampaign(campaignId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["campaign", campaignId] });
    void qc.invalidateQueries({ queryKey: ["campaigns"] });
  };
}

const ended = (c: CampaignDetail) => c.states.includes("ended") || !!c.ended_at;
/** update_campaign_schedule's "out of Draft" (0052:541-546): a live release or any terms acceptance. */
const outOfDraft = (c: CampaignDetail) =>
  !!c.active_release_version_id || !!c.terms.live_acceptance || !!c.terms.pending_acceptance;

// ---------------------------------------------------------------------------
// Name (G-14): PATCH /v1/campaigns/{id} {name}

export function NameCard({ c }: { c: CampaignDetail }) {
  const ep = useEndpoints();
  const refresh = useRefreshCampaign(c.campaign_id);
  const intent = useIntentKey();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(c.name ?? "");
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: (n: string) => ep.advertiser.renameCampaign(c.campaign_id, n, { idempotencyKey: intent.keyFor(n) }),
    onSuccess: () => {
      intent.reset();
      setEditing(false);
      refresh();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const n = name.trim();
    if (!n) return setError("Give the campaign a name.");
    if ([...n].length > 120) return setError("Keep the name to 120 characters.");
    setError(null);
    m.mutate(n);
  }

  return (
    <Card title="Name" subtitle="Only you and your team see it. Renaming doesn't affect review or serving.">
      {editing ? (
        <form className="stack" onSubmit={submit} noValidate>
          <div style={{ maxWidth: 480 }}>
            <Input label="Campaign name" value={name} onChange={(e) => setName(e.target.value)} error={error} />
          </div>
          {m.isError ? <CommandError error={m.error} /> : null}
          <div className="row">
            <Button type="submit" loading={m.isPending}>
              Save name
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)} disabled={m.isPending}>
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <div className="row">
          <strong>{c.name ?? "No name yet"}</strong>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setName(c.name ?? "");
              setEditing(true);
            }}
          >
            Rename
          </Button>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Schedule (G-15): PATCH /v1/campaigns/{id}/schedule {start, end, expected_version}

export function ScheduleCard({ c }: { c: CampaignDetail }) {
  const ep = useEndpoints();
  const refresh = useRefreshCampaign(c.campaign_id);
  const intent = useIntentKey();
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [startLocal, setStartLocal] = useState("");
  const [startChoice, setStartChoice] = useState<TimeChoice>("");
  const [endLocal, setEndLocal] = useState("");
  const [endChoice, setEndChoice] = useState<TimeChoice>("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState<UpdateScheduleResponse | null>(null);
  const start = resolveField(startLocal, startChoice, true);
  const end = resolveField(endLocal, endChoice, false);
  const guarded = outOfDraft(c);

  const m = useMutation({
    mutationFn: (body: { schedule_start_utc: string; schedule_end_utc: string | null; expected_version: number }) =>
      ep.advertiser.updateSchedule(c.campaign_id, body, { idempotencyKey: intent.keyFor(JSON.stringify(body)) }),
    onSuccess: (res) => {
      intent.reset();
      setDone(res);
      setEditing(false);
      setConfirming(false);
      refresh();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "schedule_version_conflict") refresh();
    },
  });

  function open() {
    setStartLocal(toLocalInput(c.schedule_start_utc));
    setEndLocal(toLocalInput(c.schedule_end_utc));
    setStartChoice("");
    setEndChoice("");
    setErrors({});
    setDone(null);
    m.reset();
    setEditing(true);
  }

  function body() {
    return {
      schedule_start_utc: toRfc3339Utc((start as { instant: Date }).instant),
      schedule_end_utc: end.ok && end.instant ? toRfc3339Utc(end.instant) : null,
      expected_version: c.schedule_version,
    };
  }

  function review(e: FormEvent) {
    e.preventDefault();
    const fe: Record<string, string> = {};
    if (!start.ok) fe.start = start.error;
    if (!end.ok) fe.end = end.error;
    if (start.ok && end.ok && start.instant && end.instant && end.instant <= start.instant) fe.end = "The end must be after the start.";
    setErrors(fe);
    if (Object.keys(fe).length) return;
    if (guarded) setConfirming(true);
    else m.mutate(body());
  }

  const err = m.error instanceof ApiError ? m.error : null;
  return (
    <Card
      title="Schedule"
      subtitle="Serves from the start instant up to, but not including, the end instant. Shown in your time zone and as the stored UTC instant."
    >
      <div className="stack">
        <dl className="definition-list">
          <dt>Start</dt>
          <dd>
            {formatLocal(c.schedule_start_utc)} · <code>{formatUtc(c.schedule_start_utc)}</code>
          </dd>
          <dt>End</dt>
          <dd>
            {c.schedule_end_utc ? (
              <>
                {formatLocal(c.schedule_end_utc)} · <code>{formatUtc(c.schedule_end_utc)}</code>
              </>
            ) : (
              "No end date"
            )}
          </dd>
          {c.schedule_updated_at ? (
            <>
              <dt>Last changed</dt>
              <dd>{formatLocal(c.schedule_updated_at)}</dd>
            </>
          ) : null}
        </dl>
        {done ? (
          <Banner tone="success" live="polite" title="Schedule updated">
            <div className="stack-sm">
              <span>
                In effect from {formatLocal(done.effective_at)} (<code>{formatUtc(done.effective_at)}</code>). Decisions already in flight at
                that instant complete under the schedule they started against.
              </span>
              <span className="small">
                Serving window: {done.impact.previous_serving_days ?? "open-ended"} → {done.impact.new_serving_days ?? "open-ended"} days.
                {done.impact.paused ? " The campaign is still paused — changing the schedule doesn't resume it." : ""}
              </span>
            </div>
          </Banner>
        ) : null}
        {ended(c) ? (
          <p className="small muted">This campaign has ended, so its schedule can't be changed.</p>
        ) : editing ? (
          <form className="stack" onSubmit={review} noValidate>
            {guarded ? (
              <Banner tone="warning" title="This change takes effect immediately">
                Schedule isn't part of creative or config review. You'll confirm the new times and that it's you before it's saved.
              </Banner>
            ) : null}
            <p className="small muted">
              Times are in your time zone, <strong>{viewerTimeZone()}</strong>.
            </p>
            <div className="grid-2">
              <TimeField
                label="New start"
                value={startLocal}
                onChange={(v) => (setStartLocal(v), setStartChoice(""))}
                resolved={start}
                choice={startChoice}
                onChoice={setStartChoice}
                error={errors.start}
              />
              <TimeField
                label="New end"
                optional
                value={endLocal}
                onChange={(v) => (setEndLocal(v), setEndChoice(""))}
                resolved={end}
                choice={endChoice}
                onChoice={setEndChoice}
                error={errors.end}
                hint="Leave empty to run until you end it."
              />
            </div>
            {m.isError && !confirming ? (
              <CommandError error={m.error}>
                {err?.code === "schedule_version_conflict" ? <span className="small">The current dates are shown above now.</span> : null}
              </CommandError>
            ) : null}
            <div className="row">
              <Button type="submit" loading={m.isPending && !confirming}>
                {guarded ? "Review change" : "Save schedule"}
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)} disabled={m.isPending}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div>
            <Button variant="secondary" onClick={open}>
              Change schedule
            </Button>
          </div>
        )}
      </div>
      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        dismissible={!m.isPending}
        title="Change the schedule now?"
        description="This takes effect the moment you confirm. It doesn't pause, resume or end the campaign."
        actions={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)} disabled={m.isPending}>
              Cancel
            </Button>
            <Button onClick={() => m.mutate(body())} loading={m.isPending}>
              Confirm change
            </Button>
          </>
        }
      >
        {start.ok && start.instant ? (
          <div className="stack-sm small">
            <span>
              Start: {formatUtc(c.schedule_start_utc)} → <strong>{formatUtc(start.instant)}</strong>
            </span>
            <span>
              End: {c.schedule_end_utc ? formatUtc(c.schedule_end_utc) : "no end date"} →{" "}
              <strong>{end.ok && end.instant ? formatUtc(end.instant) : "no end date"}</strong>
            </span>
            <span className="muted">
              Extending live serving uses more of your budget sooner; shortening it stops serving earlier. Budgets still cap spend.
            </span>
            {m.isError ? <CommandError error={m.error} /> : null}
          </div>
        ) : null}
      </Dialog>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Budget (G-16): PATCH /v1/campaigns/{id}/budget {lifetime_budget, daily_budget|null}

export function BudgetCard({ c }: { c: CampaignDetail }) {
  const ep = useEndpoints();
  const refresh = useRefreshCampaign(c.campaign_id);
  const intent = useIntentKey();
  const b = c.budget;
  const currency = b.currency ?? c.live?.config_version.currency ?? "USD";
  const [editing, setEditing] = useState(false);
  const [lifetime, setLifetime] = useState("");
  const [daily, setDaily] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState<UpdateBudgetResponse | null>(null);

  const m = useMutation({
    mutationFn: (body: { lifetime_budget: string; daily_budget: string | null }) =>
      ep.advertiser.updateBudget(c.campaign_id, body, { idempotencyKey: intent.keyFor(JSON.stringify(body)) }),
    onSuccess: (res) => {
      intent.reset();
      setDone(res);
      setEditing(false);
      refresh();
    },
  });

  const canAdjust = !ended(c) && (!!c.active_release_version_id || !!b.lifetime_budget);
  const lifeV = validateAmountInput(lifetime, currency);
  const dailyV = validateAmountInput(daily, currency);
  const increase =
    editing &&
    ((lifeV.ok && (!b.lifetime_budget || compareDecimal(lifeV.value, b.lifetime_budget) > 0)) ||
      (!daily.trim() && !!b.daily_budget) ||
      (dailyV.ok && (!b.daily_budget || compareDecimal(dailyV.value, b.daily_budget) > 0)));

  function open() {
    setLifetime(b.lifetime_budget ? trimZeros(b.lifetime_budget) : "");
    setDaily(b.daily_budget ? trimZeros(b.daily_budget) : "");
    setErrors({});
    setDone(null);
    m.reset();
    setEditing(true);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const fe: Record<string, string> = {};
    if (!lifetime.trim()) fe.lifetime = "Enter a lifetime budget.";
    else if (!lifeV.ok) fe.lifetime = amountValidationMessage(lifeV.reason, currency);
    if (daily.trim()) {
      if (!dailyV.ok) fe.daily = amountValidationMessage(dailyV.reason, currency);
      else if (lifeV.ok && compareDecimal(dailyV.value, lifeV.value) > 0) fe.daily = "The daily budget can't be more than the lifetime budget.";
    }
    setErrors(fe);
    if (Object.keys(fe).length || !lifeV.ok) return;
    m.mutate({ lifetime_budget: lifeV.value, daily_budget: daily.trim() && dailyV.ok ? dailyV.value : null });
  }

  const err = m.error instanceof ApiError ? m.error : null;
  const floor = err?.code === "budget_below_committed" ? err.detailString("minimum") : undefined;
  const floorPeriod = err?.detailString("period");
  return (
    <Card title="Budget" subtitle="Hard caps on spend. Every reservation is checked against them before an ad is served.">
      <div className="stack">
        {b.lifetime ? (
          <div className="grid-auto">
            <MoneyStat label="Lifetime budget" value={b.lifetime.budget_amount} currency={currency} />
            <MoneyStat label="Captured" value={b.lifetime.captured} currency={currency} note="Spent against this budget" />
            <MoneyStat label="Reserved" value={b.lifetime.reserved} currency={currency} note="Earmarked, not yet captured" alwaysFlag />
            <MoneyStat label="Remaining" value={b.lifetime.remaining} currency={currency} />
          </div>
        ) : b.lifetime_budget ? (
          <p>
            Lifetime budget <Money value={b.lifetime_budget} currency={currency} /> — nothing reserved against it yet.
          </p>
        ) : c.active_release_version_id ? null : (
          <p className="small muted">
            The budget comes from the version you submit. It takes effect when that version is promoted.
          </p>
        )}
        {b.daily_budget ? (
          <div className="stack-sm">
            <div className="grid-auto">
              <MoneyStat label="Daily budget" value={b.daily_budget} currency={currency} />
              {b.today ? (
                <>
                  <MoneyStat label="Spent today" value={b.today.spent} currency={currency} alwaysFlag note="Reserved or captured today" />
                  <MoneyStat label="Left today" value={b.today.remaining} currency={currency} />
                </>
              ) : null}
            </div>
            <span className="xs muted">
              Days run midnight to midnight in <strong>{b.budget_timezone ?? "the budget time zone"}</strong>.
              {b.today?.enforced_at_reservation ? " The daily budget is enforced on every reservation." : ""}
            </span>
          </div>
        ) : b.lifetime_budget ? (
          <p className="small muted">No daily cap.</p>
        ) : null}
        {done ? (
          <Banner tone="success" live="polite" title={done.replayed ? "Budget already updated" : "Budget updated"}>
            Lifetime <Money value={done.lifetime_budget} currency={done.currency} />,{" "}
            {done.daily_budget ? (
              <>
                daily <Money value={done.daily_budget} currency={done.currency} />
              </>
            ) : (
              "no daily cap"
            )}
            , in effect from {formatLocal(done.effective_at)}.
          </Banner>
        ) : null}
        {!canAdjust ? null : editing ? (
          <form className="stack" onSubmit={submit} noValidate>
            <div className="grid-2">
              <Input
                label={`Lifetime budget (${currency})`}
                inputMode="decimal"
                value={lifetime}
                onChange={(e) => setLifetime(e.target.value)}
                error={
                  errors.lifetime ??
                  (floor && floorPeriod === "lifetime" ? (
                    <>
                      Can't go below <Money value={floor} currency={currency} /> — that's already spent or reserved.
                    </>
                  ) : undefined)
                }
              />
              <Input
                label={`Daily budget (${currency})`}
                optional
                inputMode="decimal"
                value={daily}
                onChange={(e) => setDaily(e.target.value)}
                hint="Leave empty for no daily cap."
                error={
                  errors.daily ??
                  (floor && floorPeriod === "daily" ? (
                    <>
                      Can't go below <Money value={floor} currency={currency} /> — that's already spent or reserved today.
                    </>
                  ) : undefined)
                }
              />
            </div>
            {increase ? (
              <p className="small">Raising a budget, or removing the daily cap, increases what can be spent: you'll confirm it's you first.</p>
            ) : null}
            {m.isError && !floor ? <CommandError error={m.error} /> : null}
            <div className="row">
              <Button type="submit" loading={m.isPending}>
                Save budget
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)} disabled={m.isPending}>
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div>
            <Button variant="secondary" onClick={open}>
              Change budget
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

/** "250.0000000000" → "250", "12.5000000000" → "12.5": the exact ledger value, without the scale padding. */
export function trimZeros(v: string): string {
  return v.includes(".") ? v.replace(/0+$/, "").replace(/\.$/, "") : v;
}

// ---------------------------------------------------------------------------
// Pause / Resume / End (G-15)

type Action = "pause" | "resume" | "end";

export function ControlsCard({ c }: { c: CampaignDetail }) {
  const ep = useEndpoints();
  const toast = useToast();
  const refresh = useRefreshCampaign(c.campaign_id);
  const intent = useIntentKey();
  const [confirm, setConfirm] = useState<Action | null>(null);
  const [lastError, setLastError] = useState<{ action: Action; error: unknown } | null>(null);

  const m = useMutation({
    mutationFn: (action: Action): Promise<unknown> => {
      const opts = { idempotencyKey: intent.keyFor(action) };
      if (action === "pause") return ep.advertiser.pauseCampaign(c.campaign_id, opts);
      if (action === "resume") return ep.advertiser.resumeCampaign(c.campaign_id, opts);
      return ep.advertiser.endCampaign(c.campaign_id, opts);
    },
    onSuccess: (_res, action) => {
      intent.reset();
      setConfirm(null);
      setLastError(null);
      toast.show(action === "pause" ? "Campaign paused." : action === "resume" ? "Campaign resumed." : "Campaign ended.", "success");
      refresh();
    },
    onError: (error, action) => {
      setConfirm(null);
      setLastError({ action, error });
      if (error instanceof ApiError && isResumeBlocked(error)) refresh();
    },
  });

  if (ended(c)) {
    return (
      <Card title="Pause, resume, end">
        <p className="muted">This campaign has ended{c.ended_at ? ` (${formatLocal(c.ended_at)})` : ""}. Ending is permanent.</p>
      </Card>
    );
  }
  const paused = !!c.paused_at;
  const e = lastError?.error instanceof ApiError ? lastError.error : null;
  return (
    <Card title="Pause, resume, end" subtitle="Pause is reversible. End is permanent and is a separate action.">
      <div className="stack">
        {paused && c.eligibility.resume_blocker ? (
          <Banner tone="warning" title="Resume is blocked right now">
            {resumeBlockedMessage(c.eligibility.resume_blocker)}
          </Banner>
        ) : null}
        {lastError && e && isResumeBlocked(e) ? (
          <Banner tone="warning" live="alert" title="Not resumed">
            {resumeBlockedMessage(e.code)} The campaign is still paused.
          </Banner>
        ) : lastError ? (
          <CommandError error={lastError.error} />
        ) : null}
        <div className="row">
          {paused ? (
            <Button variant="secondary" onClick={() => setConfirm("resume")}>
              Resume
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => setConfirm("pause")}>
              Pause
            </Button>
          )}
          <Button variant="danger" onClick={() => setConfirm("end")}>
            End campaign
          </Button>
        </div>
      </div>
      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        dismissible={!m.isPending}
        alert={confirm === "end"}
        title={confirm === "pause" ? "Pause this campaign?" : confirm === "resume" ? "Resume this campaign?" : "End this campaign?"}
        description={
          confirm === "pause"
            ? "New ad decisions stop immediately. Ads already shown to users may still redeem their engagement window, so you may see a small number of billing events after pausing."
            : confirm === "resume"
              ? "Eligibility is re-checked first (destination, suspensions, the live release). If anything has changed since you paused, the campaign stays paused and you'll see why."
              : "Ending a campaign is permanent — you'll need to create a new campaign to run these ads again."
        }
        actions={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)} disabled={m.isPending}>
              Cancel
            </Button>
            <Button variant={confirm === "end" ? "danger" : "primary"} onClick={() => confirm && m.mutate(confirm)} loading={m.isPending}>
              {confirm === "pause" ? "Pause campaign" : confirm === "resume" ? "Resume campaign" : "End campaign permanently"}
            </Button>
          </>
        }
      />
    </Card>
  );
}

/** A 422 from resume_campaign's blocked outcome: error is the reason (campaign_commands.go:180-208). */
function isResumeBlocked(e: ApiError): boolean {
  return !!e.code && e.code in RESUME_BLOCKED && (e.path?.endsWith("/resume") ?? false);
}
